// src/context/auth-context.tsx
import React from "react";
import { User } from "../types/user";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useLoginMutation, useRegisterMutation, authApi } from "./auth-api";

import { client } from "./api-client";
import { useAsync } from "./hooks"; // your custom useAsync hook
import { baseApi } from "../store/api/baseApi";
import { clearAuth, setAuth } from "../store/auth-slice";

export interface AuthContextProps {
  data: User | null;
  login: (credentials: { username: string; password: string }) => Promise<User>;
  register: (payload: { password: string; token: string }) => Promise<User>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextProps | undefined>(
  undefined,
);

// A stored session is only usable if it is a user object carrying a token.
// Earlier builds wrote the raw /register response ({ user: {...} }) and even
// plain error strings into "ps-user"; such a value is truthy, so App would
// mount AuthenticatedApp for a user with no token and no role, leaving every
// request unauthenticated. Anything that fails this check is discarded.
const isStoredUser = (value: unknown): value is User =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as User).token === "string" &&
  (value as User).token !== "";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loginMutation] = useLoginMutation();
  const [registerMutation] = useRegisterMutation();
  const dispatch = useDispatch();
  const history = useHistory();
  // useAsync handles loading / error / status
  const { data, setData, run, isLoading, isError, error, isIdle } = useAsync({
    initialState: { data: null },
  });

  // Initialize from localStorage
  React.useEffect(() => {
    const stored = localStorage.getItem("ps-user");
    if (!stored) return;

    let parsed: unknown = null;
    try {
      parsed = JSON.parse(stored);
    } catch {
      parsed = null;
    }

    if (!isStoredUser(parsed)) {
      localStorage.removeItem("ps-user");
      return;
    }

    setData(parsed);

    dispatch(
      setAuth({
        user: parsed,
        token: parsed.token,
      }),
    );
  }, [setData, dispatch]);

  // Login function
  const login = React.useCallback(
    async (credentials: { username: string; password: string }) => {
      const response = await loginMutation(credentials).unwrap();

      // extract inner user
      const user = response.user;
      if (user) {
        setData(user);
        localStorage.setItem("ps-user", JSON.stringify(user));
        dispatch(
          setAuth({
            user: user,
            token: user.token,
          }),
        );
      }
      return user!;
    },
    [loginMutation, run, setData, dispatch],
  );

  // Register function
  const register = React.useCallback(
    async (payload: { password: string; token: string }) => {
      const response = await run(registerMutation(payload).unwrap());

      // Unwrap the envelope the way login does. The API also answers some
      // failures with a 200 and a bare string, so treat anything that is not a
      // real user as an error rather than persisting it as the session.
      const user = response?.user;
      if (!isStoredUser(user)) {
        throw new Error(
          typeof response === "string" && response.trim()
            ? response.trim()
            : "We could not set up your account. Please ask your administrator for a new invitation link.",
        );
      }

      setData(user);
      localStorage.setItem("ps-user", JSON.stringify(user));
      dispatch(
        setAuth({
          user: user,
          token: user.token,
        }),
      );
      return user;
    },
    [registerMutation, run, setData, dispatch],
  );

  // Logout function

  const logout = React.useCallback(async () => {
    setData(null);
    dispatch(clearAuth());
    dispatch(baseApi.util.resetApiState());
    dispatch(authApi.util.resetApiState());
    localStorage.removeItem("ps-user");
    localStorage.removeItem("PS-Reminder");
    localStorage.removeItem("PS-IsSeeOwnReview");
    localStorage.removeItem("PS-IsSameOrganization");

    history?.push("/");
  }, [dispatch]);

  const value = React.useMemo(
    () => ({ data, login, register, logout }),
    [data, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// === Hook to access auth context ===
export function useAuth(): AuthContextProps {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}

// === Hook to access client with token automatically ===
export function useClient() {
  const { data } = useAuth();
  const token = data?.token;

  return React.useCallback(
    (endpoint: string, config: any) => client(endpoint, { ...config, token }),
    [token],
  );
}
